import { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, theme, delayForLoading, LangProvider, SharedTProps, LenisProvider, PODCAST_FB_PIXEL_ID, PODCAST_LINKEDIN_PIXEL_ID, PODCAST_GA_ID } from '@acueducto/shared';
import { wide } from '@acueducto/shared/fonts';
import { ThemeProvider } from 'styled-components';
import LoadingBar, { LoadingBarRef } from 'react-top-loading-bar';
import es from 'public/locales/es/common.json';
import { PrismicPreview } from '@prismicio/next/pages';
import { repositoryName } from '../prismicio';
import { AppProps } from 'next/app';
import { NextRouter } from 'next/router';
import type { NextPage } from 'next';
import 'styles/globals.css';


interface CustomAppProps extends Omit<AppProps, 'router'> {
  router: NextRouter;
  Component: NextPage & {
    getLayout?: (page: React.ReactElement) => React.ReactNode;
  };
}

function App({ Component, pageProps, router }: CustomAppProps) {
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const LoadingBarRef = useRef<LoadingBarRef>(null);

  const sharedT = es as SharedTProps;

  // Memoizar los manejadores de eventos del router
  const handleRouteComplete = useCallback(() => {
    setTimeout(() => {
      LoadingBarRef.current?.complete();
    }, 300);
  }, []);

  const handleRouteStart = useCallback(() => {
    LoadingBarRef.current?.continuousStart();
  }, []);

  const handleRouteError = useCallback((err: Error) => {
    setTimeout(() => {
      if ('cancelled' in err && err.cancelled) {
        // console.log(`${err} on route to ${url}`);
      }
      LoadingBarRef.current?.complete();
    }, 300);
  }, []);

  useEffect(() => {
    // Deshabilitar el scroll
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100vh';

    // Animación de carga
    delayForLoading(800).then(() => {
      const bordered = document.getElementById('bordered');
      const logo = document.getElementById('logo');
      const revealer = document.getElementById('revealer');
      if (bordered && logo && revealer) {
        // Transición de salida
        bordered.classList.add('hidden');
        logo.style.opacity = '0';

        setTimeout(() => {
          revealer.style.pointerEvents = 'none';
          revealer.style.opacity = '0';
          setHasLoaded(true);
        }, 400);

        setTimeout(() => {
          // Eliminar elementos de transición del DOM
          bordered.remove();
          revealer.remove();
          logo.remove();
        }, 1000);
      }
    });

    // Agregar event listeners para la barra de carga
    router.events.on('routeChangeStart', handleRouteStart);
    router.events.on('routeChangeComplete', handleRouteComplete);
    router.events.on('routeChangeError', handleRouteError);

    // Limpieza: remover event listeners
    return () => {
      router.events.off('routeChangeStart', handleRouteStart);
      router.events.off('routeChangeComplete', handleRouteComplete);
      router.events.off('routeChangeError', handleRouteError);
    };
  }, [handleRouteStart, handleRouteComplete, handleRouteError, router.events]);

  useEffect(() => {
    if (hasLoaded) {
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    }
  }, [hasLoaded]);

  const content = Component.getLayout ? (
    Component.getLayout(<Component {...pageProps} lang={router.locale} />)
  ) : (
    <ThemeProvider theme={theme}>
      <LangProvider value={sharedT}>
        <LenisProvider>
          <LoadingBar
            ref={LoadingBarRef}
            height={3}
            color={theme.colors.accent}
            className="TopBar"
          />
          <PrismicPreview repositoryName={repositoryName}>
            <Layout
              t={sharedT}
              hasLoaded={hasLoaded}
              enableNewsletterPopup
              fbPixelId={PODCAST_FB_PIXEL_ID}
              linkedInPixelId={PODCAST_LINKEDIN_PIXEL_ID}
              gaId={PODCAST_GA_ID}
            >
              <Component {...pageProps} lang={router.locale} />
            </Layout>
          </PrismicPreview>
        </LenisProvider>
      </LangProvider>
    </ThemeProvider>
  );

  return (
    <div
      className={`${wide.variable} ${wide.className}`}
      style={{ display: 'contents' }}
    >
      {content}
    </div>
  );
}

export default App; 